import { Link } from 'react-router';
import type { AccessContext } from '@traceio-org/platform-contracts';
import { administrableContexts, isHelpDeskOnly } from './permissions';
import { settingsPath } from './scope';

/**
 * The way into the organization settings from the signed-in hub pages, for a person me/access marks as an
 * administrator of at least one organization (nothing for anyone else): straight to the organization when there is
 * one, to the list when there are several.
 */
export function settingsHeaderActions(contexts: readonly AccessContext[] | null | undefined) {
  const settings = settingsHeaderLink(contexts);
  const tickets = myTicketsLink(contexts);
  if (!settings && !tickets) return undefined;
  return (
    <>
      {tickets}
      {settings}
    </>
  );
}

/**
 * "My tickets", for everyone with a workspace (an employee context): every member files tickets and reads their
 * own, across organizations. Portal customers and staff have no tickets here.
 */
export function myTicketsLink(contexts: readonly AccessContext[] | null | undefined, className?: string) {
  if (!contexts?.some((context) => context.type === 'employee')) return undefined;
  return (
    <Link className={className} to="/tickets">
      My tickets
    </Link>
  );
}

/** The link itself, with a class for where it sits (a button-like link in a page, a plain one in the banner). */
export function settingsHeaderLink(
  contexts: readonly AccessContext[] | null | undefined,
  className?: string,
) {
  const administrable = contexts ? administrableContexts(contexts) : [];
  const [only] = administrable;
  if (!only) return undefined;
  return (
    <Link
      className={className}
      to={administrable.length === 1 ? settingsPath(only.organization.id) : '/settings'}
    >
      {/* A help desk member reaches the Help desk section only: the link says so. */}
      {administrable.every(isHelpDeskOnly) ? 'Help desk' : <>Account &amp; billing</>}
    </Link>
  );
}

/** The employee workspace's own settings link on the product chooser, when the person administers it. */
export function WorkspaceSettingsLink({ context }: { context: AccessContext }) {
  const [workspace] = administrableContexts([context]);
  if (!workspace) return null;
  return (
    <Link className="trace-auth-link" to={settingsPath(workspace.organization.id)}>
      {isHelpDeskOnly(workspace)
        ? `${workspace.organization.displayName} help desk`
        : `Manage ${workspace.organization.displayName}`}
    </Link>
  );
}
